import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LanguageContextType {
  language: 'tr' | 'en';
  setLanguage: (lang: 'tr' | 'en') => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<'tr' | 'en'>('tr');

  const translations = {
    tr: {
      // Navigation
      'nav.home': 'Ana Sayfa',
      'nav.about': 'Hakkımda',
      'nav.experience': 'Deneyim',
      'nav.education': 'Eğitim',
      'nav.skills': 'Beceriler',
      'nav.projects': 'Projeler',
      'nav.volunteer': 'Gönüllülükler',
      'nav.contact': 'İletişim',

      // Hero Section
      'hero.greeting': 'Merhaba, Ben',
      'hero.title': 'Furkan Gül',
      'hero.subtitle': 'Full Stack Developer & DevOps Engineer',
      'hero.cta.contact': 'İletişime Geç',
      'hero.cta.cv': 'CV İndir',
      'hero.cta.personality': 'Kisilik Raporu',

      // About Section
      'about.title': 'Hakkımda',
      
      // Languages
      'languages.title': 'Dil Bilgisi',
      'languages.turkish': 'Türkçe',
      'languages.english': 'İngilizce',
      'languages.russian': 'Rusça',
      'languages.native': 'Ana Dil',
      'languages.independent': 'Bağımsız Kullanıcı',
      'languages.basic': 'Temel Kullanıcı',
      
      // Skills
      'skills.title': 'Beceriler',
      'about.description': 'Full Stack Developer ve DevOps Engineer olarak modern web teknolojileri, server yönetimi ve cloud computing konularında uzmanım. Hem frontend hem backend hem de infrastructure tarafında kapsamlı deneyime sahibim.',
      'about.location': 'İstanbul, Türkiye',
      'about.role': 'Backend Developer',

      // Experience Section
      'experience.title': 'Deneyim',
      'experience.freelancer.title': 'Freelance Full Stack Developer',
      'experience.freelancer.year': 'Ocak 2024 - Devam Ediyor',
      'experience.freelancer.desc1': '19 Ocak 2024\'ten beri freelance olarak çalışıyorum',
      'experience.freelancer.desc2': 'Çeşitli projelerde full stack geliştirme hizmetleri sunuyorum',
      'experience.freelancer.desc3': 'Müşteri odaklı çözümler üretiyorum',
      'experience.sistemnetwork.title': 'Sistem Network Uzman Yardımcısı - Bilgi Sistemleri',
      'experience.sistemnetwork.year': 'Mart 2024 - Eylül 2024',
      'experience.sistemnetwork.company': 'İstanbul, Türkiye',
      'experience.sistemnetwork.desc1': 'Linux tabanlı sistemlerin kurulumu ve yönetimi',
      'experience.sistemnetwork.desc2': 'Azure, AWS ve Microsoft teknolojileri üzerinde bulut çözümleri',
      'experience.sistemnetwork.desc3': 'DevOps süreçlerinde otomasyon, sistem optimizasyonu ve sürdürülebilirlik',
      'experience.sistemnetwork.desc4': 'Database management, Zabbix ile izleme, firewall ve switch yapılandırmaları',
      'experience.sistemnetwork.desc5': 'Help Desk desteği, kullanıcı sorunlarının çözümü ve teknik destek',
      'experience.techcareer.title': 'Full Stack Developer - Tech Career Fullstack Bootcamp',
      'experience.techcareer.year': 'Aralık 2023 - Ocak 2024',
      'experience.techcareer.company': 'İstanbul, Türkiye',
      'experience.techcareer.desc1': '.NET Core ile backend ve React.js ile frontend geliştirme',
      'experience.techcareer.desc2': 'Bitirme projesi: .NET & React.js tabanlı bilet alma sitesi',
      'experience.techcareer.desc3': 'Kullanıcı kayıt/giriş, etkinlik listeleri, bilet satın alma ve ödeme akışı',
      'experience.techcareer.desc4': 'RESTful API yapısı, veri yönetimi ve kullanıcı doğrulama',
      'experience.fmss.title': 'Full Stack Developer - FMSS',
      'experience.fmss.year': 'Haziran 2022 - Eylül 2022',
      'experience.fmss.company': 'İstanbul, Türkiye',
      'experience.fmss.desc1': 'Java Spring Boot (backend) ve React.js (frontend) teknolojileri',
      'experience.fmss.desc2': 'Temiz kod prensipleri (Clean Code) ve SDLC uygulamaları',
      'experience.fmss.desc3': 'PostgreSQL veritabanı üzerinde veri modelleme ve sorgulama',
      'experience.fmss.desc4': 'Git versiyon kontrolü, branch yönetimi ve ekip içi iş birliği',
      
      // Social & Volunteer Section
      'volunteer.title': 'Sosyal Gönüllülükler',
      'volunteer.afad.title': 'AFAD Gönüllüsü',
      'volunteer.afad.year': '2021 - Devam Ediyor',
      'volunteer.afad.desc1': '2021\'den beri AFAD gönüllüsü olarak görev alıyorum',
      'volunteer.afad.desc2': 'Acil durum müdahalelerinde destek sağlıyorum',
      'volunteer.aiesec.title': 'AIESEC Adana - IGV Departmanı Lideri',
      'volunteer.aiesec.year': '2021',
      'volunteer.aiesec.desc1': 'Incoming Global Volunteer departmanında liderlik yaptım',
      'volunteer.aiesec.desc2': 'Yurtdışından gelen gönüllüleri yönettim ve koordinasyon sağladım',
      'volunteer.aiesec.desc3': 'İngilizce iletişim becerilerimi aktif olarak kullandım',
      'volunteer.aiesec.desc4': 'Kriz yönetimi ve problem çözme konularında deneyim kazandım',
      'volunteer.worktravel.title': 'Work and Travel Program',
      'volunteer.worktravel.year': '2021 - 4 Ay',
      'volunteer.worktravel.desc1': '4 aylık Work and Travel programında yurtdışında çalışma deneyimi kazandım',
      'volunteer.worktravel.desc2': 'Farklı kültürlerle çalışma ve iletişim kurma becerilerimi geliştirdim',
      'volunteer.worktravel.desc3': 'İngilizce dil becerilerimi pratikte kullanma fırsatı buldum',

      // Education Section
      'education.title': 'Eğitim',
      'education.university.title': 'Üniversite',
      'education.university.name': 'Adana Alparslan Türkeş Bilim ve Teknolojisi Üniversitesi',
      'education.university.degree': 'Lisans',
      'education.university.field': 'Bilgisayar Mühendisliği',
      'education.university.period': '2017 - 2023',
      'education.softito.title': 'İstanbul Ticaret Odası - SOFTİTO Backend Bootcamp',
      'education.softito.year': '2025 - Birincilik 🏆 (320 Saat)',
      'education.softito.description': 'Backend Developer eğitimi programında birincilik elde ettim. C# .NET, ASP.NET Core, Entity Framework Core, SQL Server, RESTful API geliştirme, Katmanlı Mimari, Clean Architecture, Identity ile kullanıcı yönetimi, Docker, Git/GitHub ve mikroservis temelleri konularında kapsamlı eğitim aldım.',

      // Projects Section
      'projects.title': 'Projeler',
      'projects.hospital.title': 'Furkan Hastanesi',
      'projects.hospital.description': 'Modern hastane yönetim sistemi',
      'projects.socket.title': 'Java Socket Project',
      'projects.socket.description': 'Java socket programlama örneği',
      'projects.website.title': 'Personal Website',
      'projects.website.description': 'Kişisel portfolyo websitesi',

      // Contact Section
      'contact.title': 'İletişim',
      'contact.subtitle': 'Benimle İletişime Geçin',
      'contact.description': 'Yeni projeler, iş fırsatları veya işbirliği fikirleri için her zaman açıkım. Aşağıdaki kanallardan bana ulaşabilirsiniz.',
      'contact.email': 'Email',
      'contact.github': 'GitHub',

      // ChatBot
      'chatbot.title': 'Furkan Gül AI Asistanı',
      'chatbot.subtitle': 'Her zaman yardımcı',
      'chatbot.description': 'Şirketler için özel olarak geliştirdiğim AI asistanım ile hakkımda detaylı bilgi alabilir, hangi pozisyonlarda işe uygun olduğumu öğrenebilirsiniz.',
      'chatbot.button': 'AI Asistanı ile Konuş',
      'chatbot.welcome': 'Merhaba! Ben Furkan Gül\'ün AI asistanıyım. Hakkında bilgi almak, hangi pozisyonlarda işe uygun olduğunu öğrenmek veya işbirliği fırsatları hakkında konuşmak için buradayım. Size nasıl yardımcı olabilirim?',

      // Footer
      'footer.copyright': '© 2024 Furkan Gül. Tüm hakları saklıdır.'
    },
    en: {
      // Navigation
      'nav.home': 'Home',
      'nav.about': 'About',
      'nav.experience': 'Experience',
      'nav.education': 'Education',
      'nav.skills': 'Skills',
      'nav.projects': 'Projects',
      'nav.volunteer': 'Volunteer Work',
      'nav.contact': 'Contact',

      // Hero Section
      'hero.greeting': 'Hello, I\'m',
      'hero.title': 'Furkan Gül',
      'hero.subtitle': 'Full Stack Developer & DevOps Engineer',
      'hero.cta.contact': 'Get In Touch',
      'hero.cta.cv': 'Download CV',
      'hero.cta.personality': 'Personality Report',

      // About Section
      'about.title': 'About Me',
      
      // Languages
      'languages.title': 'Languages',
      'languages.turkish': 'Turkish',
      'languages.english': 'English',
      'languages.russian': 'Russian',
      'languages.native': 'Native',
      'languages.independent': 'Independent User',
      'languages.basic': 'Basic User',
      
      // Skills
      'skills.title': 'Skills',
      'about.description': 'As a Full Stack Developer and DevOps Engineer, I specialize in modern web technologies, server management, and cloud computing. I have comprehensive experience in both frontend, backend, and infrastructure.',
      'about.location': 'Istanbul, Turkey',
      'about.role': 'Backend Developer',

      // Experience Section
      'experience.title': 'Experience',
      'experience.freelancer.title': 'Freelance Full Stack Developer',
      'experience.freelancer.year': 'January 2024 - Present',
      'experience.freelancer.desc1': 'Working as a freelancer since January 19, 2024',
      'experience.freelancer.desc2': 'Providing full stack development services across various projects',
      'experience.freelancer.desc3': 'Delivering customer-focused solutions',
      'experience.sistemnetwork.title': 'System Network Assistant Specialist - IT',
      'experience.sistemnetwork.year': 'March 2024 - September 2024',
      'experience.sistemnetwork.company': 'Istanbul, Turkey',
      'experience.sistemnetwork.desc1': 'Installation and management of Linux-based systems',
      'experience.sistemnetwork.desc2': 'Cloud solutions on Azure, AWS and Microsoft technologies',
      'experience.sistemnetwork.desc3': 'Automation, system optimization and sustainability in DevOps processes',
      'experience.sistemnetwork.desc4': 'Database management, monitoring with Zabbix, firewall and switch configuration',
      'experience.sistemnetwork.desc5': 'Help Desk support, user issue resolution and technical support',
      'experience.techcareer.title': 'Full Stack Developer - Tech Career Fullstack Bootcamp',
      'experience.techcareer.year': 'December 2023 - January 2024',
      'experience.techcareer.company': 'Istanbul, Turkey',
      'experience.techcareer.desc1': 'Backend with .NET Core and frontend with React.js',
      'experience.techcareer.desc2': 'Capstone project: .NET & React.js ticket booking site',
      'experience.techcareer.desc3': 'User registration/login, event listings, ticket purchase and payment flow',
      'experience.techcareer.desc4': 'RESTful API structure, data management and user authentication',
      'experience.fmss.title': 'Full Stack Developer - FMSS',
      'experience.fmss.year': 'June 2022 - September 2022',
      'experience.fmss.company': 'Istanbul, Turkey',
      'experience.fmss.desc1': 'Java Spring Boot (backend) and React.js (frontend) technologies',
      'experience.fmss.desc2': 'Clean Code principles and SDLC practices',
      'experience.fmss.desc3': 'Data modeling and querying on PostgreSQL database',
      'experience.fmss.desc4': 'Git version control, branch management and team collaboration',

      // Social & Volunteer Section
      'volunteer.title': 'Volunteer & Social',
      'volunteer.afad.title': 'AFAD Volunteer',
      'volunteer.afad.year': '2021 - Present',
      'volunteer.afad.desc1': 'Serving as an AFAD volunteer since 2021',
      'volunteer.afad.desc2': 'Supporting emergency response efforts',
      'volunteer.aiesec.title': 'AIESEC Adana - IGV Department Leader',
      'volunteer.aiesec.year': '2021',
      'volunteer.aiesec.desc1': 'Led the Incoming Global Volunteer department',
      'volunteer.aiesec.desc2': 'Managed and coordinated volunteers from abroad',
      'volunteer.aiesec.desc3': 'Actively used English communication skills',
      'volunteer.aiesec.desc4': 'Gained experience in crisis management and problem-solving',
      'volunteer.worktravel.title': 'Work and Travel Program',
      'volunteer.worktravel.year': '2021 - 4 Months',
      'volunteer.worktravel.desc1': 'Gained work experience abroad in a 4-month Work and Travel program',
      'volunteer.worktravel.desc2': 'Developed skills in working and communicating with different cultures',
      'volunteer.worktravel.desc3': 'Had the opportunity to use English language skills in practice',

      // Education Section
      'education.title': 'Education',
      'education.university.title': 'University',
      'education.university.name': 'Adana Alparslan Türkeş Science and Technology University',
      'education.university.degree': 'Bachelor\'s Degree',
      'education.university.field': 'Computer Engineering',
      'education.university.period': '2017 - 2023',
      'education.softito.title': 'Istanbul Chamber of Commerce - SOFTİTO Backend Bootcamp',
      'education.softito.year': '2025 - 1st Place 🏆 (320 Hours)',
      'education.softito.description': 'I achieved first place in the Backend Developer training program. I received comprehensive training in C# .NET, ASP.NET Core, Entity Framework Core, SQL Server, RESTful API development, Layered Architecture, Clean Architecture, Identity for user management, Docker, Git/GitHub, and microservices fundamentals.',

      // Projects Section
      'projects.title': 'Projects',
      'projects.hospital.title': 'Furkan Hospital',
      'projects.hospital.description': 'Modern hospital management system',
      'projects.socket.title': 'Java Socket Project',
      'projects.socket.description': 'Java socket programming example',
      'projects.website.title': 'Personal Website',
      'projects.website.description': 'Personal portfolio website',

      // Contact Section
      'contact.title': 'Contact',
      'contact.subtitle': 'Get In Touch With Me',
      'contact.description': 'I\'m always open to new projects, job opportunities, or collaboration ideas. You can reach me through the channels below.',
      'contact.email': 'Email',
      'contact.github': 'GitHub',

      // ChatBot
      'chatbot.title': 'Furkan Gül AI Assistant',
      'chatbot.subtitle': 'Always helpful',
      'chatbot.description': 'With my specially developed AI assistant for companies, you can get detailed information about me and learn which positions I am suitable for.',
      'chatbot.button': 'Talk to AI Assistant',
      'chatbot.welcome': 'Hello! I am Furkan Gül\'s AI assistant. I\'m here to provide information about him, learn which positions he\'s suitable for, or discuss collaboration opportunities. How can I help you?',

      // Footer
      'footer.copyright': '© 2024 Furkan Gül. All rights reserved.'
    }
  };

  const t = (key: string): string => {
    const translation = translations[language] as any;
    return translation[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
